import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPlayer } from '../../../store/video/playerSlice'
import { useMediaPlayer, useMediaState } from '@vidstack/react'
import { isMobile } from 'react-device-detect'
import usePrevious from '../../../utils/hooks/usePrevious'

const MiniModeContext = createContext<[boolean, (value: boolean) => void]>([
  false,
  () => {
    return
  },
])

export const useMiniMode = () => useContext(MiniModeContext)

type MiniModeProviderProps = {
  onMiniModeChange?: (value: boolean) => void
  children: React.ReactNode
}

const MiniModeProvider = ({ children, onMiniModeChange }: MiniModeProviderProps) => {
  const { src } = useSelector(selectPlayer)
  const player = useMediaPlayer()
  const isFullscreen = useMediaState('fullscreen')
  const [miniMode, setMiniMode] = useState(false)
  const prevMiniMode = usePrevious(miniMode)
  const prevSrcRef = useRef<string | null>(null)

  // Only reset mini mode when the video changes, not when Redux `status` changes.
  useEffect(() => {
    const body = document.querySelector('body')
    if (body) {
      body.style.overflowY = 'scroll'
    }
    if (miniMode && prevSrcRef.current !== null && prevSrcRef.current !== src) {
      miniModeOff()
    }
    prevSrcRef.current = src
  }, [src, miniMode])

  useEffect(() => {
    if (!miniMode || !isFullscreen) {
      return
    }
    player?.exitFullscreen()
  }, [miniMode, isFullscreen, player])

  useEffect(() => {
    if (prevMiniMode !== miniMode) {
      onMiniModeChange?.(miniMode)
    }
  }, [miniMode, onMiniModeChange])

  const miniModeOn = () => {
    try {
      player?.exitFullscreen()
      const playerContainer = document.querySelector('.media-player-wrapper')
      document.ontouchmove = () => {
        return true
      }
      const body = document.querySelector('body')
      if (body) {
        body.style.overflowY = 'scroll'
      }
      if (playerContainer) {
        setMiniMode(true)
        const containerStyle = `
        position: fixed;
        bottom: 0px;
        right: 0px;
        width: 33.33%;
        height: auto;
        padding: 10px;
      `
        playerContainer.setAttribute('style', containerStyle)
      }
      setMiniMode(true)
    } catch (e) {
      console.error(e)
    }
  }

  const miniModeOff = () => {
    try {
      const playerContainer = document.querySelector('.media-player-wrapper')
      if (!isMobile) {
        const body = document.querySelector('body')
        if (body) {
          body.style.overflowY = 'hidden'
        }
      }
      if (playerContainer) {
        setMiniMode(false)
        const containerStyle = `
        position: fixed;
        top: 0px;
        bottom: 0px;
        right: 0px;
        left: 0px;
      `
        playerContainer.setAttribute('style', containerStyle)
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <MiniModeContext.Provider
      value={[
        miniMode,
        (value: boolean) => {
          if (!miniMode && value) {
            miniModeOn()
          } else if (miniMode && !value) {
            miniModeOff()
          }
        },
      ]}>
      {children}
    </MiniModeContext.Provider>
  )
}

export default MiniModeProvider
